import type {
  ReploAgent,
  SandboxFile,
} from "@agent-ui/features/chat/hooks/useReploAgentConnection";

import React from "react";

import ResponsiveControls from "@agent-ui/features/page-renderer/components/navbar/ResponsiveControls";
import { useSandboxFiles } from "@agent-ui/hooks/useSandboxFiles";
import { Button } from "@replo/design-system/components/button/Button";
import { Empty } from "@replo/design-system/components/empty/Empty";
import { Input } from "@replo/design-system/components/input/Input";
import {
  Menu,
  MenuContent,
  MenuGroup,
  MenuLabel,
  MenuRadioGroup,
  MenuRadioItem,
  MenuSeparator,
  MenuTrigger,
} from "@replo/design-system/components/menu/Menu";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import {
  ArrowDown,
  ArrowRight,
  ArrowUp,
  ChevronDown,
  FolderSearch,
  Search,
} from "lucide-react";
import { useDebouncedState } from "replo-utils/react/use-debounced-state";

import PageCardSkeleton from "./PageCard/PageCardSkeleton";
import SandboxPageCard from "./SandboxPageCard";

const GRID_CONSTANTS = {
  MIN_CARD_WIDTH: 300,
} as const;

// =============================================================================
// Sort Types and Options
// =============================================================================

type SortField = "route" | "modifiedAt" | "createdAt";
type SortDirection = "asc" | "desc";

type OrderState = {
  field: SortField;
  direction: SortDirection;
};

const DEFAULT_ORDER: OrderState = {
  field: "modifiedAt",
  direction: "desc",
};

const sortByOptions: { label: string; value: SortField }[] = [
  { label: "Route", value: "route" },
  { label: "Last edited", value: "modifiedAt" },
  { label: "Created date", value: "createdAt" },
];

const alphabeticalSortOrderOptions = [
  { label: "A to Z", value: "asc" },
  { label: "Z to A", value: "desc" },
];

const chronologicalSortOrderOptions = [
  { label: "Newest First", value: "desc" },
  { label: "Oldest First", value: "asc" },
];

// =============================================================================
// Sort Functions
// =============================================================================

function sortFiles(
  files: SandboxFile[],
  field: SortField,
  direction: SortDirection,
): SandboxFile[] {
  return [...files].sort((a, b) => {
    let comparison = 0;

    if (field === "route") {
      comparison = a[field].localeCompare(b[field]);
    } else {
      // modifiedAt or createdAt - numeric comparison
      const aValue = a[field] ?? 0;
      const bValue = b[field] ?? 0;
      comparison = aValue - bValue;
    }

    return direction === "asc" ? comparison : -comparison;
  });
}

// =============================================================================
// SortDropdown Component
// =============================================================================

type SortDropdownProps = {
  order: OrderState;
  onOrderChange: (order: OrderState) => void;
};

const SortDropdown: React.FC<SortDropdownProps> = ({
  order,
  onOrderChange,
}) => {
  const getValueLabel = () => {
    const option = sortByOptions.find((opt) => opt.value === order.field);
    return option?.label ?? "Sort by";
  };

  const isAlphabeticalField = order.field === "route";

  return (
    <Menu>
      <MenuTrigger UNSAFE_className="min-w-full">
        <Button
          variant="secondary"
          size="base"
          startEnhancer={
            order.direction === "asc" ? <ArrowUp /> : <ArrowDown />
          }
          endEnhancer={<ChevronDown />}
          UNSAFE_className="min-w-full text-sm font-normal bg-sheet"
        >
          {getValueLabel()}
        </Button>
      </MenuTrigger>
      <MenuContent align="start">
        <MenuGroup>
          <MenuLabel>Sort By</MenuLabel>
          <MenuRadioGroup
            value={order.field}
            onValueChange={(value) => {
              onOrderChange({
                field: value as SortField,
                direction: order.direction,
              });
            }}
          >
            {sortByOptions.map((option) => (
              <MenuRadioItem
                key={option.value}
                value={option.value}
                label={option.label}
              />
            ))}
          </MenuRadioGroup>
        </MenuGroup>
        <MenuSeparator />
        <MenuGroup>
          <MenuLabel>Sort Order</MenuLabel>
          <MenuRadioGroup
            value={order.direction}
            onValueChange={(value) => {
              onOrderChange({
                field: order.field,
                direction: value as SortDirection,
              });
            }}
          >
            {(isAlphabeticalField
              ? alphabeticalSortOrderOptions
              : chronologicalSortOrderOptions
            ).map((option) => (
              <MenuRadioItem
                key={option.value}
                value={option.value}
                label={option.label}
              />
            ))}
          </MenuRadioGroup>
        </MenuGroup>
      </MenuContent>
    </Menu>
  );
};

// =============================================================================
// PagesSection Component
// =============================================================================

interface PagesSectionProps {
  setSelectedTab: (tab: "pages" | "templates") => void;
  agent: ReploAgent;
  previewBaseUrl: string | null;
}

function PagesSection({
  setSelectedTab,
  agent,
  previewBaseUrl,
}: PagesSectionProps) {
  const [searchQueryInputValue, searchQuery, setSearchQuery] =
    useDebouncedState("");
  const [order, setOrder] = React.useState<OrderState>(DEFAULT_ORDER);

  const projectId = useCurrentProjectId();

  const { files, isLoading } = useSandboxFiles({
    agent,
    projectId: projectId ?? null,
  });

  // Filter and sort files (client-side)
  const filteredAndSortedFiles = React.useMemo(() => {
    let result = files;

    // Apply search filter
    if (searchQuery) {
      const lowerQuery = searchQuery.toLowerCase();
      result = result.filter((file) => {
        return file.route.toLowerCase().includes(lowerQuery);
      });
    }

    // Apply sorting
    return sortFiles(result, order.field, order.direction);
  }, [files, searchQuery, order]);

  const noPages = files.length === 0 && !isLoading && searchQuery.length === 0;
  const emptySearchResults =
    filteredAndSortedFiles.length === 0 && !isLoading && searchQuery.length > 0;

  if (noPages) {
    return (
      <div className="pb-12">
        <Empty
          title="No pages created yet"
          size="base"
          description="Start building with chat or by using one of our ready-made templates."
          media={
            <img
              src="/images/empty-pages.svg"
              alt="no pages"
              className="h-16 w-24"
            />
          }
          UNSAFE_className="w-full h-full"
        >
          <Button
            variant="secondary"
            size="base"
            endEnhancer={<ArrowRight />}
            onClick={() => {
              setSelectedTab("templates");
            }}
          >
            Use a Template
          </Button>
        </Empty>
      </div>
    );
  }

  return (
    <div className="flex gap-4 flex-col h-full">
      <div className="grid grid-cols-[1fr_auto] items-center gap-2 sm:flex sm:gap-3 sm:items-center w-full flex-shrink-0">
        <div className="flex min-w-0 w-full">
          <Input
            startEnhancer={<Search size={12} />}
            placeholder="Search pages"
            size="base"
            className="grow"
            value={searchQueryInputValue}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <ResponsiveControls variant="secondary" />
        <div className="col-span-2 sm:col-span-1 sm:flex-1">
          <SortDropdown order={order} onOrderChange={setOrder} />
        </div>
      </div>
      {emptySearchResults ? (
        <Empty
          title="No pages found"
          description="Try a different search term."
          icon={<FolderSearch />}
          UNSAFE_className="py-[10%]"
        />
      ) : (
        <div
          className="grid gap-4 pt-4 pb-6"
          style={{
            gridTemplateColumns: `repeat(auto-fill, minmax(${GRID_CONSTANTS.MIN_CARD_WIDTH}px, 1fr))`,
          }}
        >
          {isLoading
            ? Array.from({ length: 6 }).map((_, index) => (
                <PageCardSkeleton key={`initial-${index}`} />
              ))
            : filteredAndSortedFiles.map((file) => (
                <SandboxPageCard
                  key={file.filepath}
                  file={file}
                  agent={agent}
                  previewBaseUrl={previewBaseUrl}
                />
              ))}
        </div>
      )}
    </div>
  );
}

export default PagesSection;
